// Word Order — a hand-drawn cheat sheet: the S–V–O flow (as a sketchy SVG you can
// cycle through real examples) plus the five verbs you'll use most, present tense.
// The SVG is roughened with an SVG filter so the boxes/arrows look hand-inked.
import { speakButtonHTML } from "../lib/audio.js";

const EXAMPLES = [
  { s: "Maria", v: "come", o: "maçãs", en: "Maria eats apples" },
  { s: "Eu", v: "tenho", o: "um cão", en: "I have a dog" },
  { s: "Eles", v: "falam", o: "português", en: "They speak Portuguese" },
  { s: "O menino", v: "lê", o: "um livro", en: "The boy reads a book" },
];

const BIG_VERBS = [
  ["ser", "to be", "sou", "és", "é"],
  ["estar", "to be", "estou", "estás", "está"],
  ["ter", "to have", "tenho", "tens", "tem"],
  ["ir", "to go", "vou", "vais", "vai"],
  ["fazer", "to do/make", "faço", "fazes", "faz"],
];

let exIdx = 0;

function diagramSVG(ex) {
  return `
    <svg viewBox="0 0 660 235" class="w-full h-auto max-w-2xl mx-auto" role="img" aria-label="Portuguese word order: Subject, Verb, Object">
      <defs>
        <filter id="wo-rough"><feTurbulence type="fractalNoise" baseFrequency="0.013" numOctaves="2" seed="7" result="n"/><feDisplacementMap in="SourceGraphic" in2="n" scale="3.5"/></filter>
        <marker id="wo-arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="strokeWidth"><path d="M0 0 L8 3 L0 6 z" fill="#b08968"></path></marker>
      </defs>
      <g filter="url(#wo-rough)">
        <rect x="30" y="26" width="160" height="60" rx="16" fill="#d1fae5" stroke="#b08968" stroke-width="2.5"></rect>
        <rect x="250" y="26" width="160" height="60" rx="16" fill="#fef3c7" stroke="#b08968" stroke-width="2.5"></rect>
        <rect x="470" y="26" width="160" height="60" rx="16" fill="#e0f2fe" stroke="#b08968" stroke-width="2.5"></rect>
        <path d="M192 56 H250" stroke="#b08968" stroke-width="2.5" fill="none" marker-end="url(#wo-arrow)"></path>
        <path d="M412 56 H470" stroke="#b08968" stroke-width="2.5" fill="none" marker-end="url(#wo-arrow)"></path>
      </g>
      <g font-family="Patrick Hand, sans-serif">
        <text x="110" y="63" text-anchor="middle" font-size="18" fill="#065f46">SUBJECT</text>
        <text x="330" y="63" text-anchor="middle" font-size="18" fill="#92400e">VERB</text>
        <text x="550" y="63" text-anchor="middle" font-size="18" fill="#075985">OBJECT</text>
        <text x="110" y="140" text-anchor="middle" font-size="23" fill="#1f2937">${ex.s}</text>
        <text x="330" y="140" text-anchor="middle" font-size="23" fill="#1f2937">${ex.v}</text>
        <text x="550" y="140" text-anchor="middle" font-size="23" fill="#1f2937">${ex.o}</text>
        <text x="330" y="172" text-anchor="middle" font-size="15" fill="#9ca3af">${ex.en}</text>
        <text x="330" y="212" text-anchor="middle" font-size="14" fill="#6b7280">Subject → Verb → Object — the safe default, like English.</text>
      </g>
    </svg>`;
}

function render(app) {
  const ex = EXAMPLES[exIdx];
  const verbRows = BIG_VERBS.map(
    ([inf, en, eu, tu, ele]) => `
      <tr class="border-b border-dashed border-amber-300/50 last:border-0">
        <td class="py-1 pr-3 font-bold text-amber-700 whitespace-nowrap">${inf}${speakButtonHTML(inf)}</td>
        <td class="py-1 px-2 text-sage-400 text-sm" style="font-family:Inter,sans-serif">${en}</td>
        <td class="py-1 px-2 text-sage-800">${eu}</td>
        <td class="py-1 px-2 text-sage-800">${tu}</td>
        <td class="py-1 px-2 text-sage-800">${ele}</td>
      </tr>`
  ).join("");

  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Word Order 🧩</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">How a Portuguese sentence is built — and the five verbs you'll lean on the most.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg">
        ${diagramSVG(ex)}
        <div class="text-center mt-2">
          <button id="wo-next" class="text-sm px-3 py-1.5 rounded-full border border-sage-200 text-sage-600 hover:border-sage-400" style="font-family:Inter,sans-serif">Show another example →</button>
        </div>
        <h3 class="text-2xl text-amber-700 sketch-underline inline-block mt-6 mb-2">The big verbs · present tense</h3>
        <div class="overflow-x-auto">
          <table class="w-full text-lg">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-400" style="font-family:Inter,sans-serif">
              <th class="py-1 pr-3">Verb</th><th class="py-1 px-2"></th><th class="py-1 px-2">eu</th><th class="py-1 px-2">tu</th><th class="py-1 px-2">ele/ela</th>
            </tr></thead>
            <tbody>${verbRows}</tbody>
          </table>
        </div>
      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">The one exception to learn early: adjectives go <b>after</b> the noun — <i>uma casa branca</i>.</p>
    </section>`;

  app.querySelector("#wo-next").addEventListener("click", () => {
    exIdx = (exIdx + 1) % EXAMPLES.length;
    render(app);
  });
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export function wordOrderView(app) {
  exIdx = 0;
  render(app);
}
